import { useState } from 'react';
import { GuideThumb } from './ExerciseGuidePreview';
import { catalogExercisesForMuscle, displayNameForGuide } from './exerciseGuide';

const muscleDataDa = {
  chest: { label: 'Bryst', info: 'Pectoralis major og minor. Nøglen til skubbebevægelser. Fokuser på fuld strækning i bunden.' },
  shoulders: { label: 'Skuldre', info: 'Tre hoveder: front, side og bagside. De fleste overtræner forsiden og undertræner bagsiden. Prioriter lateral raises og face pulls.' },
  biceps: { label: 'Biceps', info: 'To hoveder - kort og lang. Træn med både supineret og hammer greb for fuld udvikling. Strækning i bunden er nøglen.' },
  triceps: { label: 'Triceps', info: 'Tre hoveder - lang, lateral og medial. Det lange hoved trænes bedst med overhead bevægelser. Udgør 2/3 af din overarm.' },
  abs: { label: 'Mave', info: 'Rectus abdominis, skrå mavemuskler og transversus abdominis. Synlig mave er mest kost - men træning forbedrer stabilitet markant.' },
  quads: { label: 'Quad', info: 'Fire muskler foran låret. Dyb squat og benstrækninger rammer dem godt.' },
  back: { label: 'Ryg', info: 'Lats, rhomboids, traps og erectors. Den mest undertrænte muskelgruppe for begyndere. Træk ligeså meget som du skubber.' },
  hamstrings: { label: 'Baglår', info: 'Bedst trænet med hoftehængselbevægelser. Ofte forsømt sammenlignet med quad - en almindelig skaderisiko.' },
  glutes: { label: 'Baller', info: 'Den største muskelgruppe i kroppen. Hip thrust er kongeøvelsen - spring den ikke over.' },
  calves: { label: 'Lægge', info: 'Notorisk svære at udvikle. Kræver højt volumen og fuld bevægeudslag - helt op og helt ned.' },
  traps: { label: 'Trapezmuskler', info: 'Øvre, midtre og nedre trapezmuskel. Ofte overdrevet øverst fra shrugs men undertrænet i mid og nedre del.' },
};
const muscleData = {
  chest: { label: 'Chest', info: 'The pectoralis major and minor. Key for pushing movements. Often undertrained in depth - focus on full stretch at the bottom.' },
  shoulders: { label: 'Shoulders', info: 'Three heads: front, side and rear. Most people overtrain the front and undertrain the rear. Prioritise lateral raises and face pulls.' },
  biceps: { label: 'Biceps', info: 'Two heads - short and long. Train with both supinated and hammer grip for full development. Stretch at the bottom is key.' },
  triceps: { label: 'Triceps', info: 'Three heads - long, lateral and medial. The long head is best trained overhead. Makes up 2/3 of your upper arm.' },
  abs: { label: 'Abs', info: 'Rectus abdominis, obliques and transverse abs. Visible abs are mostly diet - but training them improves core stability significantly.' },
  quads: { label: 'Quads', info: 'Four muscles at the front of the thigh. Deep squats and leg extensions hit them well.' },
  calves: { label: 'Calves', info: 'Gastrocnemius and soleus. Notoriously stubborn to grow. Need high volume and full range of motion.' },
  back: { label: 'Back', info: 'Lats, rhomboids, traps and erectors. The most undertrained muscle group for beginners. Pull as much as you push.' },
  hamstrings: { label: 'Hamstrings', info: 'Best trained with hip hinge movements. Often neglected compared to quads - a common injury risk.' },
  glutes: { label: 'Glutes', info: 'The largest muscle group in the body. Hip thrust is the king exercise - do not skip it.' },
  traps: { label: 'Traps', info: 'Upper, middle and lower trapezius. Often overdeveloped on top from shrugs but undertrained in the mid and lower portions.' },
};

const ACCENT = '#FF6B00';
const HOVER = 'rgba(255,107,0,0.5)';
const DEFAULT = 'rgba(255,255,255,0.08)';
const STROKE = 'rgba(255,255,255,0.2)';

function heatFill(value, max) {
  if (!max || !value) return DEFAULT;
  const t = Math.min(1, value / max);
  const alpha = 0.18 + t * 0.82;
  return `rgba(255, 107, 0, ${alpha.toFixed(3)})`;
}

export default function BodyMap({ language, heat, weekHeat, t, onPickExercise, onOpenExercise }) {
  const [selected, setSelected] = useState(null);
  const [view, setView] = useState('front');
  const [hovered, setHovered] = useState(null);

  const data = language === 'da' ? muscleDataDa : muscleData;
  const muscle = selected ? data[selected] : null;
  const heatMap = heat || {};
  const maxHeat = Math.max(0, ...Object.values(heatMap).filter((v) => typeof v === 'number'));
  const weekVal = selected && weekHeat ? weekHeat[selected] : null;

  const fill = (key) => {
    if (selected === key) return ACCENT;
    if (hovered === key) return HOVER;
    if (heat) return heatFill(heatMap[key] || 0, maxHeat);
    return DEFAULT;
  };

  const mp = (key) => ({
    fill: fill(key),
    stroke: STROKE,
    strokeWidth: 1,
    cursor: 'pointer',
    transition: 'fill 0.2s',
    onClick: () => setSelected(selected === key ? null : key),
    onMouseEnter: () => setHovered(key),
    onMouseLeave: () => setHovered(null),
  });

  return (
    <div>
      <div className="bodymap-toggle">
        <button type="button" className={`btn btn-dark period-btn${view === 'front' ? ' is-active' : ''}`} onClick={() => { setView('front'); setSelected(null); }}>{language === 'da' ? 'Forside' : 'Front'}</button>
        <button type="button" className={`btn btn-dark period-btn${view === 'back' ? ' is-active' : ''}`} onClick={() => { setView('back'); setSelected(null); }}>{language === 'da' ? 'Bagside' : 'Back'}</button>
      </div>

      {/* SVG Body */}
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px' }}>
        {view === 'front' ? (
          <svg width="200" height="420" viewBox="0 0 200 420">
            {/* Head */}
            <ellipse cx="100" cy="30" rx="22" ry="26" fill={DEFAULT} stroke={STROKE} strokeWidth="1" />
            {/* Neck */}
            <rect x="91" y="54" width="18" height="16" fill={DEFAULT} stroke={STROKE} strokeWidth="1" />
            {/* Shoulders */}
            <ellipse cx="62" cy="76" rx="20" ry="12" {...mp('shoulders')} />
            <ellipse cx="138" cy="76" rx="20" ry="12" {...mp('shoulders')} />
            {/* Chest */}
            <path d="M80 70 Q100 65 120 70 L122 105 Q100 110 78 105 Z" {...mp('chest')} />
            {/* Abs */}
            <path d="M84 106 Q100 102 116 106 L114 160 Q100 164 86 160 Z" {...mp('abs')} />
            {/* Left Bicep */}
            <path d="M44 82 Q36 90 35 115 Q42 120 50 115 Q52 90 58 82 Z" {...mp('biceps')} />
            {/* Right Bicep */}
            <path d="M156 82 Q164 90 165 115 Q158 120 150 115 Q148 90 142 82 Z" {...mp('biceps')} />
            {/* Left Tricep (side) */}
            <path d="M58 82 Q66 90 65 115 Q58 118 52 115 Q50 90 44 82 Z" {...mp('triceps')} />
            {/* Right Tricep (side) */}
            <path d="M142 82 Q134 90 135 115 Q142 118 148 115 Q150 90 156 82 Z" {...mp('triceps')} />
            {/* Forearms */}
            <path d="M35 116 Q30 130 32 150 Q40 154 48 150 Q50 130 50 116 Z" fill={DEFAULT} stroke={STROKE} strokeWidth="1" />
            <path d="M165 116 Q170 130 168 150 Q160 154 152 150 Q150 130 150 116 Z" fill={DEFAULT} stroke={STROKE} strokeWidth="1" />
            {/* Hands */}
            <ellipse cx="40" cy="158" rx="10" ry="12" fill={DEFAULT} stroke={STROKE} strokeWidth="1" />
            <ellipse cx="160" cy="158" rx="10" ry="12" fill={DEFAULT} stroke={STROKE} strokeWidth="1" />
            {/* Hips */}
            <path d="M80 160 Q100 156 120 160 L124 190 Q100 194 76 190 Z" fill={DEFAULT} stroke={STROKE} strokeWidth="1" />
            {/* Left Quad */}
            <path d="M80 190 Q88 188 96 190 L94 255 Q86 260 78 255 Z" {...mp('quads')} />
            {/* Right Quad */}
            <path d="M104 190 Q112 188 120 190 L122 255 Q114 260 106 255 Z" {...mp('quads')} />
            {/* Left Calf */}
            <path d="M78 258 Q86 255 94 258 L92 320 Q85 325 77 320 Z" {...mp('calves')} />
            {/* Right Calf */}
            <path d="M106 258 Q114 255 122 258 L120 320 Q113 325 107 320 Z" {...mp('calves')} />
            {/* Feet */}
            <ellipse cx="85" cy="326" rx="12" ry="8" fill={DEFAULT} stroke={STROKE} strokeWidth="1" />
            <ellipse cx="115" cy="326" rx="12" ry="8" fill={DEFAULT} stroke={STROKE} strokeWidth="1" />
          </svg>
        ) : (
          <svg width="200" height="420" viewBox="0 0 200 420">
            {/* Head */}
            <ellipse cx="100" cy="30" rx="22" ry="26" fill={DEFAULT} stroke={STROKE} strokeWidth="1" />
            {/* Neck */}
            <rect x="91" y="54" width="18" height="16" fill={DEFAULT} stroke={STROKE} strokeWidth="1" />
            {/* Traps */}
            <path d="M80 68 Q100 60 120 68 L118 88 Q100 84 82 88 Z" {...mp('traps')} />
            {/* Shoulders back */}
            <ellipse cx="62" cy="80" rx="20" ry="12" {...mp('shoulders')} />
            <ellipse cx="138" cy="80" rx="20" ry="12" {...mp('shoulders')} />
            {/* Back */}
            <path d="M80 88 Q100 84 120 88 L122 155 Q100 160 78 155 Z" {...mp('back')} />
            {/* Left Tricep back */}
            <path d="M44 86 Q36 100 35 125 Q44 130 54 125 Q58 100 62 86 Z" {...mp('triceps')} />
            {/* Right Tricep back */}
            <path d="M156 86 Q164 100 165 125 Q156 130 146 125 Q142 100 138 86 Z" {...mp('triceps')} />
            {/* Forearms */}
            <path d="M35 126 Q30 140 32 158 Q40 162 48 158 Q50 140 54 126 Z" fill={DEFAULT} stroke={STROKE} strokeWidth="1" />
            <path d="M165 126 Q170 140 168 158 Q160 162 152 158 Q150 140 146 126 Z" fill={DEFAULT} stroke={STROKE} strokeWidth="1" />
            {/* Hands */}
            <ellipse cx="40" cy="166" rx="10" ry="12" fill={DEFAULT} stroke={STROKE} strokeWidth="1" />
            <ellipse cx="160" cy="166" rx="10" ry="12" fill={DEFAULT} stroke={STROKE} strokeWidth="1" />
            {/* Glutes */}
            <path d="M78 158 Q100 154 122 158 L120 200 Q100 206 80 200 Z" {...mp('glutes')} />
            {/* Left Hamstring */}
            <path d="M80 200 Q88 198 96 200 L94 258 Q86 263 78 258 Z" {...mp('hamstrings')} />
            {/* Right Hamstring */}
            <path d="M104 200 Q112 198 120 200 L122 258 Q114 263 106 258 Z" {...mp('hamstrings')} />
            {/* Left Calf */}
            <path d="M78 260 Q86 257 94 260 L92 320 Q85 325 77 320 Z" {...mp('calves')} />
            {/* Right Calf */}
            <path d="M106 260 Q114 257 122 260 L120 320 Q113 325 107 320 Z" {...mp('calves')} />
            {/* Feet */}
            <ellipse cx="85" cy="326" rx="12" ry="8" fill={DEFAULT} stroke={STROKE} strokeWidth="1" />
            <ellipse cx="115" cy="326" rx="12" ry="8" fill={DEFAULT} stroke={STROKE} strokeWidth="1" />
          </svg>
        )}
      </div>

      {/* Info panel */}
      {muscle && (
        <div className="bodymap-info">
          <h3 className="bodymap-info-title">{muscle.label}</h3>
          {heat && (
            <p className="bodymap-heat-stat">
              {(heatMap[selected] || 0).toFixed(1)} {language === 'da' ? 'sæt i perioden' : 'sets in period'}
              {weekVal != null ? ` · ${weekVal.toFixed(1)} ${language === 'da' ? 'denne uge' : 'this week'}` : ''}
            </p>
          )}
          <p className="bodymap-info-text">{muscle.info}</p>
          <p className="bodymap-info-label">{t?.topExercises || (language === 'da' ? 'Top øvelser' : 'Top exercises')}</p>
          <div className="bodymap-catalog">
            {catalogExercisesForMuscle(selected, 8).map((ex) => {
              const name = displayNameForGuide(ex);
              return (
                <div key={ex.slug} className="bodymap-catalog-row">
                  <GuideThumb name={name} onOpen={onOpenExercise} size={40} />
                  <button
                    type="button"
                    className="bodymap-catalog-pick"
                    onClick={() => onPickExercise?.(name)}
                  >
                    <span className="bodymap-catalog-name">{name}</span>
                    <span className="bodymap-catalog-eq">{ex.equipment}</span>
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}